import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'

import Langs, { Localize } from '../../../app/lang/langs'
import { showErrorToast, showSuccessToast } from '../../../app/utils/helpers'
import { importVLearnQuestions } from '../../../containers/vlearn/vlearnSlice'
import { vlearnType } from '../vlearnUi'

const MAX_OPTIONS = 6

/** The UTF-8 byte-order mark, spelled out rather than pasted so it survives every editor. */
const BOM = '\uFEFF'

/** The header the parser expects, in order. */
const TEMPLATE_HEADER = [
    'question_text', 'question_type', 'points',
    'option_1', 'option_2', 'option_3', 'option_4', 'option_5', 'option_6',
    'option_1_correct', 'option_2_correct', 'option_3_correct',
    'option_4_correct', 'option_5_correct', 'option_6_correct',
    'explanation'
].join(';')

/**
 * V-Learning — bulk import.
 *
 * ── Why semicolons, and why they must stay ──────────────────────────────────────────
 *
 * The template is semicolon-separated, exactly as `QuizImport.js` does it. On a
 * French-locale Windows the list separator IS the semicolon, so Excel opens this file
 * straight into columns; a comma-separated file lands in a single column and the teacher
 * has to walk the import wizard before they can type anything. The people who use this are
 * on French Excel, so the separator is a product decision rather than a formatting detail.
 * A UTF-8 byte-order mark is prepended for the same audience: without it Excel reads the
 * accented question text as Latin-1 and "sécurité" arrives as "sÃ©curitÃ©".
 *
 * ── Why the client validates rows it is about to send anyway ─────────────────────────
 *
 * `importQuestions` validates every row through the same `validateQuestionShape` the typed
 * form goes through, and reports the bad ones rather than failing the batch. Checking here
 * first is not distrust of that — it is so the author sees which of their sixty rows is
 * wrong BEFORE half the quiz is created, while the file is still open next to them.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {number} props.idQuiz
 * @param {number} props.idCourse
 * @param {Function} props.onClose
 * @param {Function} props.onImported
 */
export default function QuizImportDialog({ open, idQuiz, idCourse, onClose, onImported }) {
    const dispatch = useDispatch()
    const lang = useSelector((state) => state.settings.lang)

    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

    const fileInputRef = useRef(null)
    const [raw, setRaw] = useState('')
    const [importing, setImporting] = useState(false)
    /** Errors the SERVER reported, keyed by the CSV line they belong to. */
    const [serverErrors, setServerErrors] = useState({})
    const [imported, setImported] = useState(null)

    const parsed = useMemo(() => parseRows(raw, lang), [raw, lang])
    const validRows = parsed.filter((row) => !row.error)
    const invalidRows = parsed.filter((row) => row.error)

    const resetInput = useCallback((value) => {
        setRaw(value)
        setServerErrors({})
        setImported(null)
    }, [])

    // ── Template ────────────────────────────────────────────────────────────────────

    const handleDownloadTemplate = useCallback(() => {
        const example = [
            Localize('VLEARN_IMPORT_SAMPLE_QUESTION', lang),
            'single', '1',
            Localize('VLEARN_IMPORT_SAMPLE_OPTION_1', lang),
            Localize('VLEARN_IMPORT_SAMPLE_OPTION_2', lang),
            Localize('VLEARN_IMPORT_SAMPLE_OPTION_3', lang),
            '', '', '',
            'TRUE', 'FALSE', 'FALSE', '', '', '',
            Localize('VLEARN_IMPORT_SAMPLE_EXPLANATION', lang)
        ].join(';')

        // The BOM is the reason accents survive a round trip through Excel — see the
        // docblock. CRLF for the same audience: Excel on Windows expects it.
        const content = `${BOM}${TEMPLATE_HEADER}\r\n${example}\r\n`
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'vlearn-questions-template.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }, [lang])

    // ── File ────────────────────────────────────────────────────────────────────────

    const handleFile = useCallback(async (event) => {
        const file = event.target.files && event.target.files[0]
        // Cleared immediately so re-picking the same file after a fix still fires a change.
        event.target.value = ''
        if (!file) return

        const name = file.name.toLowerCase()
        if (!name.endsWith('.csv') && !name.endsWith('.txt')) {
            showErrorToast(Localize('VLEARN_IMPORT_TOAST_BAD_FILE', lang))
            return
        }
        try {
            const text = await file.text()
            resetInput(text)
        } catch (error) {
            showErrorToast(Localize('VLEARN_IMPORT_TOAST_READ_FAILED', lang))
        }
    }, [lang, resetInput])

    // ── Commit ──────────────────────────────────────────────────────────────────────

    const handleImport = useCallback(async () => {
        if (!validRows.length || importing) return
        setImporting(true)
        setServerErrors({})

        const action = await dispatch(importVLearnQuestions({
            id_course: idCourse,
            id_quiz: idQuiz,
            questions: validRows.map((row) => row.question)
        }))
        setImporting(false)

        const data = action && action.payload && action.payload.responseData
        if (!data || data.status !== 1) {
            const message = action && action.payload && action.payload.responseError
                && action.payload.responseError.data && action.payload.responseError.data.error
            showErrorToast(message || Localize('VLEARN_IMPORT_TOAST_FAILED', lang))
            return
        }

        setImported(Number(data.imported) || 0)

        if (Array.isArray(data.errors) && data.errors.length) {
            // The server numbers its errors by position in the array IT received, which is
            // the list of rows that passed the check above — not by line in the file. The
            // rejected rows were filtered out before sending, so the two numberings differ
            // and mapping them back is what puts each message on the row that caused it.
            const byLine = {}
            for (const entry of data.errors) {
                const row = validRows[Number(entry.line) - 1]
                if (row) byLine[row.line] = entry.error
            }
            setServerErrors(byLine)
            showErrorToast(Localize(data.errors.length === 1
                ? 'VLEARN_IMPORT_TOAST_PARTIAL_ONE'
                : 'VLEARN_IMPORT_TOAST_PARTIAL_MANY', lang).replace('%n%', data.errors.length))
            if (onImported && Number(data.imported) > 0) onImported(data)
            return
        }

        showSuccessToast(Localize(Number(data.imported) === 1
            ? 'VLEARN_IMPORT_TOAST_DONE_ONE'
            : 'VLEARN_IMPORT_TOAST_DONE_MANY', lang).replace('%n%', Number(data.imported) || 0))
        if (onImported) onImported(data)
    }, [dispatch, idCourse, idQuiz, importing, lang, onImported, validRows])

    return (
        <Dialog
            open={!!open}
            onClose={(event, reason) => {
                // A form holding typed input must not be dismissed by a stray click on the
                // backdrop or a reflex Escape: that discarded everything, with no warning and
                // no undo. Closing is an explicit choice now — Cancel, or the save button.
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
                onClose()
            }}
            fullWidth
            maxWidth="md"
            fullScreen={isSmall}
            slotProps={{ paper: { sx: { borderRadius: isSmall ? 0 : '18px' } } }}
        >
            <DialogTitle sx={{ ...vlearnType.sectionTitle, pb: 1 }}>
                <Langs str="VLEARN_IMPORT_TITLE" />
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    <Typography sx={{ ...vlearnType.body }}>
                        <Langs str="VLEARN_IMPORT_INTRO" />
                    </Typography>

                    <Box sx={{
                        padding: '14px 16px', borderRadius: '12px',
                        backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)'
                    }}>
                        <Typography sx={{ ...vlearnType.meta, fontWeight: 700, mb: 0.75 }}>
                            <Langs str="VLEARN_IMPORT_COLUMNS_TITLE" />
                        </Typography>
                        <Box
                            component="pre"
                            sx={{
                                margin: 0, overflowX: 'auto', fontSize: '0.74rem',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                color: 'var(--text-secondary, #4b5263)'
                            }}
                        >
                            {TEMPLATE_HEADER}
                        </Box>
                        <Typography sx={{ ...vlearnType.meta, mt: 1 }}>
                            <Langs str="VLEARN_IMPORT_COLUMNS_HELP" />
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button
                            variant="outlined"
                            onClick={handleDownloadTemplate}
                            startIcon={<DownloadRoundedIcon />}
                            sx={outlinedButtonSx}
                        >
                            <Langs str="VLEARN_IMPORT_BTN_TEMPLATE" />
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            startIcon={<UploadFileRoundedIcon />}
                            sx={outlinedButtonSx}
                        >
                            <Langs str="VLEARN_IMPORT_BTN_PICK_FILE" />
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.txt,text/csv"
                            onChange={handleFile}
                            hidden
                            aria-hidden="true"
                            tabIndex={-1}
                        />
                    </Stack>

                    <TextField
                        label={Localize('VLEARN_IMPORT_FIELD_PASTE', lang)}
                        value={raw}
                        onChange={(event) => resetInput(event.target.value)}
                        helperText={<Langs str="VLEARN_IMPORT_FIELD_PASTE_HELP" />}
                        fullWidth
                        multiline
                        minRows={5}
                        maxRows={12}
                        // Monospace so a misplaced semicolon is visible in the paste box.
                        inputProps={{ style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem' } }}
                    />

                    {parsed.length ? (
                        <>
                            <Divider />
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                <Chip
                                    icon={<CheckCircleRoundedIcon sx={{ fontSize: '17px !important' }} />}
                                    label={Localize(validRows.length === 1
                                        ? 'VLEARN_IMPORT_READY_ONE'
                                        : 'VLEARN_IMPORT_READY_MANY', lang).replace('%n%', validRows.length)}
                                    sx={{
                                        borderRadius: '9px', fontWeight: 600, fontSize: '0.78rem',
                                        backgroundColor: 'rgba(58,156,96,0.12)', color: '#1F7A4C'
                                    }}
                                />
                                {invalidRows.length ? (
                                    <Chip
                                        icon={<ErrorOutlineRoundedIcon sx={{ fontSize: '17px !important' }} />}
                                        label={Localize(invalidRows.length === 1
                                            ? 'VLEARN_IMPORT_REJECTED_ONE'
                                            : 'VLEARN_IMPORT_REJECTED_MANY', lang).replace('%n%', invalidRows.length)}
                                        sx={{
                                            borderRadius: '9px', fontWeight: 600, fontSize: '0.78rem',
                                            backgroundColor: 'rgba(251,105,98,0.14)', color: '#B3271F'
                                        }}
                                    />
                                ) : null}
                            </Stack>

                            <Box sx={{ overflowX: 'auto', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                                <Table size="small" sx={{ minWidth: 680 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headCellSx}><Langs str="VLEARN_IMPORT_COL_LINE" /></TableCell>
                                            <TableCell sx={headCellSx}><Langs str="VLEARN_IMPORT_COL_QUESTION" /></TableCell>
                                            <TableCell sx={headCellSx}><Langs str="VLEARN_IMPORT_COL_TYPE" /></TableCell>
                                            <TableCell sx={headCellSx} align="right"><Langs str="VLEARN_IMPORT_COL_POINTS" /></TableCell>
                                            <TableCell sx={headCellSx} align="right"><Langs str="VLEARN_IMPORT_COL_OPTIONS" /></TableCell>
                                            <TableCell sx={headCellSx}><Langs str="VLEARN_IMPORT_COL_STATUS" /></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsed.map((row) => {
                                            const failure = row.error || serverErrors[row.line] || null
                                            return (
                                                <TableRow
                                                    key={row.line}
                                                    sx={failure ? { backgroundColor: 'rgba(251,105,98,0.06)' } : undefined}
                                                >
                                                    <TableCell sx={bodyCellSx}>{row.line}</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, maxWidth: 260 }}>
                                                        <Typography sx={{
                                                            fontSize: '0.8rem',
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                        }}>
                                                            {row.preview.text || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>{row.preview.type}</TableCell>
                                                    <TableCell sx={bodyCellSx} align="right">{row.preview.points}</TableCell>
                                                    <TableCell sx={bodyCellSx} align="right">
                                                        {`${row.preview.optionCount} (${row.preview.correctCount}✓)`}
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        {failure ? (
                                                            <Typography sx={{ fontSize: '0.78rem', color: '#B3271F', fontWeight: 600 }}>
                                                                {/* A client-side problem is a localized key; a
                                                                    server message arrives already worded and is
                                                                    shown verbatim rather than mapped to a key
                                                                    that may not exist. */}
                                                                {row.error ? Localize(row.error, lang) : failure}
                                                            </Typography>
                                                        ) : (
                                                            <Typography sx={{ fontSize: '0.78rem', color: '#1F7A4C', fontWeight: 600 }}>
                                                                <Langs str="VLEARN_IMPORT_STATUS_OK" />
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </>
                    ) : null}

                    {imported !== null ? (
                        <Alert severity={Object.keys(serverErrors).length ? 'warning' : 'success'} sx={{ borderRadius: '12px' }}>
                            <Langs
                                str={imported === 1 ? 'VLEARN_IMPORT_RESULT_ONE' : 'VLEARN_IMPORT_RESULT_MANY'}
                                params={{ n: imported }}
                            />
                        </Alert>
                    ) : null}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} sx={quietButtonSx}>
                    <Langs str="VLEARN_IMPORT_BTN_CLOSE" />
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleImport}
                    disabled={!validRows.length || importing}
                    startIcon={importing ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                    sx={primaryButtonSx}
                >
                    <Langs
                        str={importing ? 'VLEARN_IMPORT_BTN_IMPORTING' : 'VLEARN_IMPORT_BTN_IMPORT'}
                        params={{ n: validRows.length }}
                    />
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// ── Parsing ─────────────────────────────────────────────────────────────────────────

/**
 * Turns the pasted or uploaded text into rows ready for `importVLearnQuestions`.
 *
 * The rules applied are `validateQuestionShape`'s, restated: text required, 2–6 options,
 * exactly 2 for `true_false`, at least one correct, exactly one correct for `single` and
 * `true_false`. `line` counts lines of the file the author is looking at, header included,
 * so the number in the preview is the number in their spreadsheet.
 *
 * @param {string} text
 * @param {string} lang only used to give the true/false pair readable labels
 * @returns {Array<{line: number, error: string|null, question: object|null, preview: object}>}
 */
function parseRows(text, lang) {
    if (!text || !String(text).trim()) return []

    // Excel writes a BOM; left in place it becomes part of the first header name and every
    // lookup against `question_text` silently misses.
    const cleaned = String(text).replace(/^\uFEFF/, '')
    const lines = cleaned.split(/\r?\n/)

    let headers = null
    const rows = []

    for (let index = 0; index < lines.length; index++) {
        const line = lines[index]
        if (!line || !line.trim()) continue

        const cells = line.split(';').map((cell) => cell.trim())

        // The header is recognised rather than assumed to be line 1, so a file that opens
        // with a blank row or a title line still imports.
        if (!headers) {
            if (cells.some((cell) => cell.toLowerCase() === 'question_text')) {
                headers = cells.map((cell) => cell.toLowerCase())
                continue
            }
            // No header at all: fall back to the template's own column order, which is what
            // a paste of just the data rows looks like.
            headers = TEMPLATE_HEADER.split(';')
        }

        const row = {}
        headers.forEach((header, position) => { row[header] = cells[position] === undefined ? '' : cells[position] })

        rows.push(buildRow(row, index + 1, lang))
    }

    return rows
}

function buildRow(row, line, lang) {
    const questionText = (row.question_text || '').trim()
    const rawType = (row.question_type || 'single').toLowerCase()
    const type = ['single', 'multiple', 'true_false'].includes(rawType) ? rawType : 'single'

    const parsedPoints = parseInt(row.points, 10)
    const points = Number.isInteger(parsedPoints) && parsedPoints > 0 ? Math.min(parsedPoints, 65535) : 1

    const options = []
    for (let position = 1; position <= MAX_OPTIONS; position++) {
        const optionText = (row[`option_${position}`] || '').trim()
        if (!optionText) continue
        options.push({ option_text: optionText, is_correct: isTruthyCell(row[`option_${position}_correct`]) })
    }

    // A true_false row may leave the option columns blank and mean the obvious thing, so the
    // pair is materialised for it. Anything else with fewer than two options is an error.
    if (type === 'true_false' && !options.length) {
        options.push(
            { option_text: Localize('VLEARN_QUESTION_TRUE', lang), is_correct: isTruthyCell(row.option_1_correct) },
            { option_text: Localize('VLEARN_QUESTION_FALSE', lang), is_correct: isTruthyCell(row.option_2_correct) })
    }

    const correctCount = options.filter((option) => option.is_correct).length
    const preview = { text: questionText, type, points, optionCount: options.length, correctCount }

    let error = null
    if (!questionText) error = 'VLEARN_IMPORT_ERR_NO_TEXT'
    else if (type === 'true_false' && options.length !== 2) error = 'VLEARN_IMPORT_ERR_TRUE_FALSE_TWO'
    else if (options.length < 2) error = 'VLEARN_IMPORT_ERR_TOO_FEW_OPTIONS'
    else if (correctCount < 1) error = 'VLEARN_IMPORT_ERR_NO_CORRECT'
    else if ((type === 'single' || type === 'true_false') && correctCount !== 1) error = 'VLEARN_IMPORT_ERR_ONE_CORRECT'

    return {
        line,
        error,
        preview,
        question: error ? null : {
            question_text: questionText,
            type,
            points,
            explanation: (row.explanation || '').trim() || null,
            options
        }
    }
}

/**
 * What counts as "correct" in a spreadsheet cell.
 *
 * `VRAI` is included because that is literally what French Excel writes into a boolean
 * cell, and a template that rejects the value Excel itself produced would be unusable for
 * the people this feature is for.
 */
function isTruthyCell(value) {
    const cell = String(value === undefined || value === null ? '' : value).trim().toUpperCase()
    return cell === 'TRUE' || cell === 'VRAI' || cell === '1' || cell === 'X' || cell === 'OUI' || cell === 'YES'
}

const headCellSx = {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: 'var(--text-muted, #8b92a0)',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    whiteSpace: 'nowrap'
}

const bodyCellSx = {
    fontSize: '0.8rem',
    color: 'var(--text-secondary, #4b5263)',
    borderBottom: '1px solid rgba(0,0,0,0.05)'
}

const primaryButtonSx = {
    borderRadius: '12px',
    px: 3,
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 650,
    fontSize: '0.9rem',
    backgroundColor: 'var(--orange, rgb(255,144,21))',
    color: '#fff',
    '&:hover': { backgroundColor: '#e07d0d' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' },
    '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.34)' }
}

const outlinedButtonSx = {
    borderRadius: '12px',
    px: 2.5,
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    borderColor: 'rgba(0,0,0,0.14)',
    '&:hover': { borderColor: 'rgba(0,0,0,0.28)', backgroundColor: 'rgba(0,0,0,0.03)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}

const quietButtonSx = {
    borderRadius: '12px',
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}
